import { useMobile } from "@/hooks";
import trackClevertapEvents from "@/utils/trackClevertapEvents";
import {
  Button,
  Modal,
  RadioButtonList,
  Toast,
} from "@monsterindia/global-ui-components";
import React, { useEffect, useState } from "react";

import {
  deactivateAccount,
  masterDataSearch,
} from "@/components/settings/api/networkUtils";
import {
  DEACTIVATION_QUESTION,
  RIO_LOGOUT_PATH,
  deactivateAccountContent,
  deactivateAccountRadioDescription,
} from "@/components/settings/contents/LinkAccounts";

import { DeactivateAccountResponse, ReasonList } from "../../types/setting";

const DeactivateAccount = () => {
  const isMobile = useMobile();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedReason, setSelectedReason] = useState<ReasonList | null>();
  const [textAreaValue, setTextAreaValue] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);
  const [toast, setToast] = useState({ text: "", type: "", show: false });
  const [deactivateReasonList, setDeactivateReasonList] =
    useState<ReasonList[]>();

  const getDescription = (item: {
    name?: string;
    deactivationResponse?: string;
  }) => {
    let id: string | undefined = item?.name
      ?.replaceAll(" ", "")
      ?.replaceAll(".", "")
      ?.toLowerCase();
    if (id && deactivateAccountRadioDescription[id]) {
      return deactivateAccountRadioDescription[id];
    } else {
      if (item?.deactivationResponse) {
        return item.deactivationResponse;
      }
    }
    return "";
  };

  const onSuccess = () => {
    var ctProps = {
      account_deactivated: "deactivated account",
    };
    setSelectedReason(null);
    setTextAreaValue("");
    trackClevertapEvents.eventPush("DeactivateAndLogout", ctProps);
    setTimeout(function () {
      window.location.href = RIO_LOGOUT_PATH;
    }, 1000);
  };

  const onDeactivateAccount = async () => {
    if (selectedReason) {
      let payload = {
        reason: {
          id: selectedReason?.id,
          text: selectedReason?.reasonName,
        },
        reasonDetail: textAreaValue,
      };

      if (selectedReason.reasonName === "Others" && !textAreaValue?.length) {
        return;
      }

      try {
        const data: DeactivateAccountResponse =
          await deactivateAccount(payload);
        if (data) {
          trackClevertapEvents.eventPush("Settings Updated", {
            deactivateAccount: payload.reason.text,
          });
          onSuccess();
        }
        setToast({
          text: data?.successMessage || "Account deactivated successfully",
          type: "success",
          show: true,
        });
        onToggleModal();
      } catch (err: any) {
        setSelectedReason(null);
        setToast({
          text:
            err?.response?.data?.errorMessage ||
            "Something went wrong. Please try again.",
          type: "error",
          show: true,
        });
      }
    } else {
      setToast({
        text: "Please select an option",
        type: "error",
        show: true,
      });
    }
  };

  const onToggleModal = () => {
    setOpenModal(!openModal);
  };

  const onReasonChange = (e: { value: string }) => {
    setSelectedReason(
      deactivateReasonList?.find((el: ReasonList) => e.value == el.uuid),
    );
  };

  const handleChange = (event: { target: { value: string } }) => {
    setTextAreaValue(event.target.value);
    if (event.target.value.length > 0) {
      setShowError(false);
    }
  };

  useEffect(() => {
    const getDeActivateReasonlist = async () => {
      try {
        const masterData = await masterDataSearch(DEACTIVATION_QUESTION);
        let list: ReasonList[] = [];
        (masterData?.data ?? []).forEach((ele: any) => {
          list.push({
            id: ele?.id ?? "",
            displayName: (
              <div>
                {ele?.name ?? ""} <br />
                <div
                  className="text-content-tertiary text-xs"
                  dangerouslySetInnerHTML={{
                    __html: getDescription(ele) ?? "",
                  }}
                ></div>
              </div>
            ),
            value: ele?.uuid ?? "",
            uuid: ele?.uuid ?? "",
            kiwiId: ele?.kiwiId ?? "",
            reasonName: ele?.name ?? "",
            dResponse: ele?.deactivationResponse ?? "",
          });
        });
        setDeactivateReasonList(list);
      } catch (err: any) {
        setToast({
          text: "Can't load reason list",
          type: "error",
          show: true,
        });
      }
    };
    getDeActivateReasonlist();
  }, []);

  return (
    <>
      <Toast
        timer={3000}
        showToast={toast.show}
        text={toast.text}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
        width={isMobile ? "75" : "50"}
        position="top-center"
      />
      <Modal
        showModal={openModal}
        closeModal={() => setOpenModal(false)}
        screenAlignment={isMobile ? "bottom-sheet" : "center"}
        style={{ height: "auto" }}
        showCloseButton={true}
        showHandleBar={true}
        customClass="!px-0 !py-2 !h-max"
      >
        <div className="md:w-[740px]">
          <div className="flex items-center px-3 pt-2 text-base font-bold">
            {deactivateAccountContent?.popupHeading ?? "Deactivate Account"}
          </div>
          {isMobile && (
            <div className="bg-border-moderate h-[1px] w-full"></div>
          )}
          <div className="flex flex-col items-center">
            <div>
              <div className="h-[75vh] overflow-y-scroll p-4">
                <div className="mb-6 pb-2 text-xs">
                  {deactivateAccountContent?.popupSubheading ??
                    "Please select a reason for deactivation."}
                </div>
                <div className="mb-16 flex flex-col gap-4">
                  {deactivateReasonList?.length ? (
                    <RadioButtonList
                      className="deactivate-radio"
                      options={deactivateReasonList}
                      label="Please select a reason"
                      layout="col"
                      size="large"
                      error={false}
                      disabled={false}
                      eventChangeHandler={(e: any) => onReasonChange(e)}
                    />
                  ) : (
                    <div className="text-content-tertiary text-center text-sm">
                      {deactivateAccountContent?.loadingReasons ??
                        "Loading reasons..."}
                    </div>
                  )}
                  {selectedReason?.reasonName == "Others" && (
                    <div className="flex flex-col gap-2">
                      <textarea
                        autoFocus
                        className="!border-border-moderate rounded-md border p-3"
                        value={textAreaValue}
                        rows={5}
                        id="input"
                        placeholder="Please help us improve our services"
                        style={{
                          height: "120px",
                          border: "1px solid",
                          resize: "none",
                        }}
                        onChange={handleChange}
                      />
                      {!textAreaValue.length && (
                        <p className="text-border-error text-xs">
                          Please specify the reason
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modalFooterFix modal_footer !bottom-1 flex justify-around gap-5 md:justify-end">
              <Button
                onClick={onToggleModal}
                label="Keep it activate"
                modifier="tertiary"
                className="w-full whitespace-nowrap md:w-auto"
              />
              <Button
                onClick={onDeactivateAccount}
                label="Deactivate"
                modifier="primary"
                size="lg"
                className="w-full whitespace-nowrap px-10 md:w-auto"
              />
            </div>
          </div>
        </div>
      </Modal>
      <div className="bg-surface-secondary flex flex-col gap-3 rounded-xl p-3">
        <div className="border-border-moderate border-b py-3 text-base font-semibold">
          {deactivateAccountContent?.settingsPageHeading ?? "Account Settings"}
        </div>
        <div className="text-content-secondary text-sm font-normal">
          {deactivateAccountContent?.settingsPageSubheading ??
            "Manage your preferences and deactivate your account if needed."}
        </div>
        <div
          className="mt-3 cursor-pointer text-sm font-bold text-[#6e00be]"
          onClick={onToggleModal}
        >
          {deactivateAccountContent?.settingsPageDeactivateCTA ??
            "Deactivate my account"}
        </div>
      </div>
    </>
  );
};

export default DeactivateAccount;
